import React from 'react';
import { Controller, FieldValues, UseFormRegister, RegisterOptions, Control, useFormContext } from 'react-hook-form';
import { useAppSelector } from '../../../store/hooks/StoreHooks';

export interface DynamicTextInputProps {
    name: string;
    label?: string;
    placeholder?: string;
    type?: React.HTMLInputTypeAttribute;
    className?: string;
    register?: UseFormRegister<FieldValues>;
    rules?: RegisterOptions;
    control?: Control<FieldValues>;
    error?: string;
    render?: (field: any) => React.ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    helperText?: string;
    unit?: string;
    [key: string]: any;
    bgColor?: string;
    inputWidth?: string;
}

const TextInput: React.FC<DynamicTextInputProps> = ({
    name,
    label,
    placeholder,
    type = 'text',
    className,
    register,
    rules,
    control,
    error,
    render,
    disabled,
    readOnly,
    helperText,
    unit,
    bgColor = "bg-darkcolor-700",
    value,
    onChange,
    onBlur,
    inputWidth,
    ...rest
}) => {
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
    const isDisabled = disabled || (customerEnabled && !customerCreated) || projectReadOnly;

    const baseInputClasses = `flex rounded-[8px] border-[1px] border-darkcolor-400 w-full ${bgColor ? bgColor : "bg-transparent"} text-neutral-25 focus:outline-none text-[16px] ${!isDisabled && "hover:border-primary-500 focus:ring-primary-500"} focus-within:ring-1 focus-within:border-primary-500 `;
    const getInputClassName = (hasError?: boolean) => {
        const errorClasses = hasError ? ' border-red-500 hover:border-red-500 focus:ring-red-500 focus-within:border-red-500' : '';
        const disabledClasses = isDisabled ? ' opacity-50 cursor-not-allowed' : `${bgColor}`;
        const customerDisabledClasses = (customerEnabled && !customerCreated) || projectReadOnly ? ' text-neutral-200 cursor-not-allowed hover:border-neutral-600' : '';
        return `${baseInputClasses}${errorClasses}${disabledClasses}${customerDisabledClasses}`.trim();
    };

    const formContext = useFormContext();
    const controlFromContext: Control<FieldValues> | undefined = control ?? (formContext ? (formContext.control as Control<FieldValues>) : undefined);
    const registerFromContext: UseFormRegister<FieldValues> | undefined = register ?? (formContext ? (formContext.register as UseFormRegister<FieldValues>) : undefined);

    // Simple input filtering based on rules
    const filterInput = (inputValue: string): string => {
        if (!rules) return inputValue;

        const r: any = rules;
        let filteredValue = inputValue;

        // Handle allowDecimal rule
        if (r.allowDecimal === false) {
            // Only allow integers (digits and optional minus sign)
            if (filteredValue.startsWith('-')) {
                filteredValue = '-' + filteredValue.substring(1).replace(/[^0-9]/g, '');
            } else {
                filteredValue = filteredValue.replace(/[^0-9]/g, '');
            }
        } else if (r.allowDecimal === true) {
            // Allow decimals but ensure valid format
            filteredValue = filteredValue.replace(/[^0-9.]/g, '');

            // Ensure only one decimal point
            const decimalCount = (filteredValue.match(/\./g) || []).length;
            if (decimalCount > 1) {
                const firstDecimalIndex = filteredValue.indexOf('.');
                filteredValue = filteredValue.substring(0, firstDecimalIndex + 1) +
                    filteredValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
            }

            if (r.allowNegative === true) {
                const negativeValue = filteredValue.replace(/[^0-9.-]/g, '');
                // Ensure minus sign is only at the beginning
                if (negativeValue.includes('-') && negativeValue.indexOf('-') > 0) {
                    filteredValue = filteredValue.replace(/-/g, '');
                }
            }
        }

        // Handle maxLength
        if (typeof r.maxLength === 'number' && filteredValue.length > r.maxLength) {
            filteredValue = filteredValue.substring(0, r.maxLength);
        }

        return filteredValue;
    };

    // Enhanced onChange handler
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, fieldOnChange?: (value: any) => void) => {
        const inputValue = e.target.value;
        const filteredValue = filterInput(inputValue);

        if (filteredValue !== inputValue) {
            const syntheticEvent = { ...e, target: { ...e.target, value: filteredValue } };
            fieldOnChange?.(filteredValue);
            onChange?.(syntheticEvent);
        } else {
            fieldOnChange?.(inputValue);
            onChange?.(e);
        }
    };

    return (
        <div className={className}>
            {label && <label htmlFor={name} className={`flex justify-start items-start mb-1 text-sm ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`}>
                {label}
                {(rest.required || rules?.required) && <span className="text-error-400 ml-1">*</span>}
            </label>}

            {controlFromContext ? (
                <Controller
                    name={name}
                    control={controlFromContext}
                    rules={rules}
                    render={({ field, fieldState }) => (
                        <div className={getInputClassName(!!(fieldState?.error || error))}>
                            <input
                                id={name}
                                type={type}
                                placeholder={placeholder}
                                {...field}
                                value={value ?? field.value ?? ''}
                                onChange={(e) => handleInputChange(e, field.onChange)}
                                onBlur={(e) => { onBlur && onBlur(e) }}
                                aria-invalid={!!(fieldState?.error || error)}
                                disabled={isDisabled}
                                readOnly={readOnly}
                                className={`flex-grow px-[12px] py-[10px] max-h-[44px] text-neutral-25 focus:outline-none w-[${inputWidth ? inputWidth : "100%"}] ${isDisabled
                                    ? "bg-transparent text-neutral-200 cursor-not-allowed rounded-[8px]"
                                    : "bg-transparent"
                                    } 
                                ${readOnly
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : ""
                                    }`}
                            />
                            {unit && (
                                <span
                                    className={`pr-[12px] py-[10px] whitespace-nowrap ${((customerEnabled && !customerCreated) || projectReadOnly) ? "text-neutral-200" : "text-gray-400"}`}
                                    style={{
                                        pointerEvents: "none",
                                        color: `${isDisabled || !field?.value ? "#E5E7EB" : "#FFFFFF"}`,
                                    }}
                                >
                                    {unit}
                                </span>
                            )}
                        </div>
                    )}
                />
            ) : registerFromContext ? (
                <div className={getInputClassName(!!error)}>
                    <input
                        id={name}
                        type={type}
                        placeholder={placeholder}
                        {...registerFromContext(name, rules)}
                        onChange={(e) => handleInputChange(e)}
                        aria-invalid={!!error}
                        disabled={isDisabled}
                        readOnly={readOnly}
                        {...rest}
                        className={`flex-grow max-h-[44px] text-neutral-25 focus:outline-none ${isDisabled
                            ? "bg-transparent text-neutral-200 cursor-not-allowed rounded-[8px]"
                            : "bg-transparent"
                            } 
                                ${readOnly
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : ""
                            }`}
                    />
                    {unit && (
                        <span
                            className={`px-3 whitespace-nowrap ${((customerEnabled && !customerCreated) || projectReadOnly) ? "text-neutral-200" : "text-gray-400"}`}
                            style={{
                                pointerEvents: "none",
                                color: `${isDisabled ? "#E5E7EB" : "#FFFFFF"}`,
                            }}
                        >
                            {unit}
                        </span>
                    )}
                </div>
            ) : (
                <div className={getInputClassName(!!error)}>
                    <input
                        id={name}
                        type={type}
                        placeholder={placeholder}
                        onChange={(e) => handleInputChange(e)}
                        aria-invalid={!!error}
                        disabled={isDisabled}
                        readOnly={readOnly}
                        {...rest}
                        className={`flex-grow max-h-[44px] text-neutral-25 focus:outline-none ${isDisabled
                            ? "bg-transparent text-neutral-200 cursor-not-allowed rounded-[8px]"
                            : "bg-transparent"
                            } 
                                ${readOnly
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : ""
                            }`}
                    />
                    {unit && (
                        <span
                            className={`px-3 whitespace-nowrap ${((customerEnabled && !customerCreated) || projectReadOnly) ? "text-neutral-200" : "text-gray-400"}`}
                            style={{
                                pointerEvents: "none",
                                color: `${isDisabled ? "#E5E7EB" : "#FFFFFF"}`,
                            }}
                        >
                            {unit}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default TextInput;